// ─────────────────────────────────────────────────────────────────────────────
//  Generic exporters for attendance report definitions.
//
//  Each works on a report definition `{ label, columns, rows }` where every
//  column exposes `header`, `align`, `width` (mm hint for PDF) and `value(row)`.
//  Meta carries { companyInfo, dateRangeLabel, dateFrom, dateTo }.
// ─────────────────────────────────────────────────────────────────────────────
import jsPDF from 'jspdf';

const slug = (s) => (s || 'rapport').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const fileBase = (report, meta) => {
  const { dateFrom, dateTo } = meta;
  const range = dateFrom === dateTo ? dateFrom : `${dateFrom}_${dateTo}`;
  return `${slug(report.label)}_${range || 'export'}`;
};

const download = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── CSV ──────────────────────────────────────────────────────────────────────
export function exportReportCSV(report, meta = {}) {
  const headers = report.columns.map((c) => c.header);
  const rows = report.rows.map((r) => report.columns.map((c) => String(c.value(r) ?? '').replace(/;/g, ',')));
  const csv = [headers, ...rows].map((row) => row.join(';')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  download(blob, `${fileBase(report, meta)}.csv`);
}

// ── Excel (HTML table that Excel opens) ──────────────────────────────────────
export function exportReportExcel(report, meta = {}) {
  const { companyInfo, dateRangeLabel } = meta;
  const company = companyInfo?.legal_name || companyInfo?.trade_name || '';
  const cols = report.columns;
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  let html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="UTF-8">
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
    <x:ExcelWorksheet><x:Name>${esc(report.label).substring(0, 31)}</x:Name><x:WorksheetOptions>
    <x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>
    </x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    </head><body><table>`;

  if (company) html += `<tr><td colspan="${cols.length}" style="font-size:14px;font-weight:bold;color:#1E40AF;padding:6px">${esc(company)}</td></tr>`;
  html += `<tr><td colspan="${cols.length}" style="font-size:12px;font-weight:bold;padding:4px 6px">${esc(report.label)}</td></tr>`;
  html += `<tr><td colspan="${cols.length}" style="color:#6B7280;padding:4px 6px">Période : ${esc(dateRangeLabel)} — ${report.rows.length} enregistrement(s)</td></tr>`;
  html += `<tr>${cols.map((c) => `<th style="background:#EFF6FF;font-weight:bold;color:#1E40AF;border:1px solid #93C5FD;padding:5px 8px">${esc(c.header)}</th>`).join('')}</tr>`;

  report.rows.forEach((r, i) => {
    const bg = i % 2 === 1 ? '#F9FAFB' : '#FFFFFF';
    html += `<tr style="background:${bg}">${cols.map((c) => `<td style="border:1px solid #E5E7EB;padding:4px 8px;text-align:${c.align === 'center' ? 'center' : 'left'}">${esc(c.value(r))}</td>`).join('')}</tr>`;
  });

  html += '</table></body></html>';
  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  download(blob, `${fileBase(report, meta)}.xls`);
}

// ── PDF (jsPDF, generic table) ───────────────────────────────────────────────
export async function exportReportPDF(report, meta = {}) {
  const { companyInfo, dateRangeLabel } = meta;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const usableW = pageW - margin * 2;
  let y = margin;

  // Normalize column widths to fill the page
  const rawTotal = report.columns.reduce((sum, c) => sum + (c.width || 20), 0);
  const cols = report.columns.map((c) => ({ ...c, w: ((c.width || 20) / rawTotal) * usableW }));

  const companyName = companyInfo?.legal_name || companyInfo?.trade_name || null;

  if (companyInfo?.logo_url) {
    try {
      const logoImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = companyInfo.logo_url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = logoImg.width;
      canvas.height = logoImg.height;
      canvas.getContext('2d').drawImage(logoImg, 0, 0);
      const logoW = 30;
      const logoH = (logoImg.height / logoImg.width) * logoW;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', pageW - margin - logoW, margin, logoW, logoH);
    } catch { /* ignore logo errors */ }
  }

  if (companyName) {
    pdf.setFontSize(15);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    pdf.text(companyName, margin, y + 7);
    pdf.setDrawColor(30, 64, 175);
    pdf.setLineWidth(0.5);
    pdf.line(margin, y + 9, margin + pdf.getTextWidth(companyName), y + 9);
    y += 13;
  }

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text(report.label, margin, y + 6);

  pdf.setFontSize(8.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);
  pdf.text(`Période : ${dateRangeLabel} — ${report.rows.length} enregistrement(s)`, margin, y + 13);
  y += 20;

  const rowH = 6.5, headerH = 8;

  const drawHeader = (startY) => {
    pdf.setFillColor(239, 246, 255);
    pdf.rect(margin, startY, usableW, headerH, 'F');
    pdf.setDrawColor(147, 197, 253);
    pdf.setLineWidth(0.3);
    pdf.rect(margin, startY, usableW, headerH, 'S');
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 64, 175);
    let x = margin;
    cols.forEach((col) => {
      if (x > margin) { pdf.setDrawColor(190, 220, 250); pdf.line(x, startY, x, startY + headerH); }
      pdf.text(col.header, col.align === 'center' ? x + col.w / 2 : x + 1.5, startY + 5.5,
        { align: col.align === 'center' ? 'center' : 'left' });
      x += col.w;
    });
    return startY + headerH;
  };

  const drawRow = (row, startY, isEven) => {
    if (isEven) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(margin, startY, usableW, rowH, 'F');
    }
    pdf.setFontSize(6.8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    let x = margin;
    cols.forEach((col) => {
      if (x > margin) {
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.1);
        pdf.line(x, startY, x, startY + rowH);
      }
      let val = String(col.value(row) ?? '');
      const maxChars = Math.floor(col.w / 1.9);
      if (val.length > maxChars) val = val.substring(0, maxChars - 1) + '…';
      pdf.text(val, col.align === 'center' ? x + col.w / 2 : x + 1.5, startY + 4.5,
        { align: col.align === 'center' ? 'center' : 'left' });
      x += col.w;
    });
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.1);
    pdf.rect(margin, startY, usableW, rowH, 'S');
    return startY + rowH;
  };

  y = drawHeader(y);
  if (report.rows.length === 0) {
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    pdf.text(report.emptyText || 'Aucune donnée.', margin + 2, y + 6);
  } else {
    report.rows.forEach((row, idx) => {
      if (y + rowH > pageH - margin) {
        pdf.addPage();
        y = margin;
        y = drawHeader(y);
      }
      y = drawRow(row, y, idx % 2 === 1);
    });
  }

  pdf.save(`${fileBase(report, meta)}.pdf`);
}
